import type { MultiPolygon, Polygon } from "geojson";
import type { APIError, ImportStatus, PERMISSION } from ".";

export enum CaseCreationType {
  NORMAL = "NORMAL",
  PREDEFINED = "PREDEFINED",
}

export enum CaseCreationStatus {
  PREPARATION = "PREPARATION",
  RETRIEVE_REGION = "RETRIEVE_REGION",
  CREATE_GRID_CONFIGURATIONS = "CREATE_GRID_CONFIGURATIONS",
  CREATE_POI_COLLECTION = "CREATE_POI_COLLECTION",
  CREATE_BASE_SCENARIO = "CREATE_BASE_SCENARIO",
  CREATE_NETWORK = "CREATE_NETWORK",
  CREATE_FLEET = "CREATE_FLEET",
  CREATE_BACKGROUND_METEO = "CREATE_BACKGROUND_METEO",
  CREATE_POINT_SOURCES = "CREATE_POINT_SOURCES",
  FINISHED = "FINISHED",
  FAILED = "FAILED",
}

export enum CaseStatus {
  PREPARING = "PREPARING",
  CREATION_FINISHED = "CREATION_FINISHED",
  CREATION_FAILED = "CREATION_FAILED",
  CALCULATING = "CALCULATING",
  CALCULATION_FINISHED = "CALCULATION_FINISHED",
  CALCULATION_FAILED = "CALCULATION_FAILED",
  CALCULATION_CANCELLED = "CALCULATION_CANCELLED",
  CALCULATION_CANCELLING = "CALCULATION_CANCELLING",
}

export enum CaseCopyStatus {
  COPYING = "COPYING",
  FINISHED = "FINISHED",
  FAILED = "FAILED",
}

export enum TimefactorType {
  RUSH_HOUR = "RUSH_HOUR",
  DAILY_AVERAGE = "DAILY_AVERAGE",
  YEARLY_TOTAL = "YEARLY_TOTAL",
}

// CaseBase is the overview case result, more details are proviced in Case
export interface CaseBase {
  id: number;
  name: string;
  caseCreationType: CaseCreationType;
  status: CaseStatus;
  creationErrors: APIError[];
  ospmEnabled: boolean;
  runYear: number;
  cutoff: number;
  description?: string;
  models: string;
  runErrors: {
    scenarioId: number;
    scenarioName: string;
    errors: APIError[];
  }[];
  nbScenarios: number;
  nbPois: number;
  dateCreated: Date;
  copyStatus: CaseCopyStatus;
  copyErrors: APIError[];
  sharedEmailList: string[];
  sharedCase: boolean;
  sharedCaseOwner?: string;
  permissions: {
    copyScenario: PERMISSION;
    createScenario: PERMISSION;
    delete: PERMISSION;
    poi: PERMISSION;
    update: PERMISSION;
    share: PERMISSION;
    updateGeneralSettings: PERMISSION;
    updateLineBasedGrid: PERMISSION;
    updatePointBasedGrid: PERMISSION;
    updateRegularGrid: PERMISSION;
  };
}

export interface Case extends CaseBase {
  description: string;
  userSrid: number;
  timefactorType: TimefactorType;
  poiImportErrors: APIError[];
  poiImportStatus: ImportStatus;
  poiImportWarnings: APIError[];
  buffer_frontendSrid: string;
}

export interface CaseShapeUploadResponse {
  polygon_4326: Polygon | MultiPolygon;
  warningList: string[];
}

export interface PollutantInfo {
  substanceId: number;
  substanceName: string;
  substanceUnit: string;
  mapTypeKey: "NORMAL" | "RELATIVE_DIFFERENCE";
  mapType: string;
  minMean: number;
  maxMean: number;
}

export interface ScenarioInfo {
  id: number;
  name: string;
  pollutantList: PollutantInfo[];
}

export interface CaseInfo {
  id: number;
  scenarioInfoList: ScenarioInfo[];
}

export type CASE_SHARE_FILTER = "ALL" | "SHARED_BY_ME" | "SHARED_WITH_ME";
